import { router } from "@inertiajs/react"
import { useInertiaCable } from "@inertia-cable/react"
import { AppShell } from "@/components/layout/app-shell"
import { PageHeader } from "@/components/layout/page-header"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"

type Kpis = {
  totalConvos: number
  resolvedConvos: number
  resolvedPct: number
  escalatedCount: number
  escalatedPct: number
  totalCostUsd: number
  avgCostUsd: number
  ratingsCount: number
  positivePct: number
}

type TopTopic = { key: string; name: string; count: number }
type LeaderboardRow = {
  agentId: number
  name: string
  key: string
  convos: number
  resolvedPct: number
  ratingsCount: number
  positivePct: number
  costUsd: number
}
type StatusMixRow = { state: string; count: number; pct: number }
type RecentActivity = { id: number; title: string; state: string; agent: string; updatedAt: string }

type Data = {
  rangeDays: number
  kpis: Kpis
  topTopics: TopTopic[]
  agentLeaderboard: LeaderboardRow[]
  statusMix: StatusMixRow[]
  recentActivity: RecentActivity[]
}

type Props = {
  range: "7d" | "30d" | "90d"
  data: Data
  analytics_stream: string
}

const RANGES: Array<{ key: Props["range"]; label: string }> = [
  { key: "7d",  label: "7 days" },
  { key: "30d", label: "30 days" },
  { key: "90d", label: "90 days" },
]

export default function AnalyticsIndex({ range, data, analytics_stream }: Props) {
  // Live updates: any AgentConvo in this company changing pings the page.
  // Debounce is generous because analytics tolerates seconds-of-staleness.
  useInertiaCable(analytics_stream, {
    only: ["data"],
    debounce: 1000,
  })

  function setRange(key: Props["range"]) {
    router.visit(`/analytics?range=${key}`, {
      preserveState: true,
      preserveScroll: true,
    })
  }

  return (
    <AppShell>
      <PageHeader
        eyebrow="Analytics"
        title="Operations dashboard"
        subtitle={`Last ${data.rangeDays} days · ${data.kpis.totalConvos.toLocaleString()} conversations`}
        actions={
          <div className="flex gap-2">
            {RANGES.map((r) => (
              <Button
                key={r.key}
                variant={r.key === range ? "red" : "ghost"}
                size="sm"
                onClick={() => setRange(r.key)}
              >
                {r.label}
              </Button>
            ))}
          </div>
        }
      />

      {/* KPI tiles */}
      <section className="grid gap-4 md:grid-cols-4">
        <KpiTile label="Conversations"    value={data.kpis.totalConvos.toLocaleString()} hint={`${data.kpis.escalatedCount} escalated`} />
        <KpiTile label="Resolved"         value={`${data.kpis.resolvedPct}%`}            hint={`${data.kpis.resolvedConvos.toLocaleString()} resolved`} />
        <KpiTile label="Total spend"      value={fmtMoney(data.kpis.totalCostUsd)}        hint={`avg ${fmtMoney(data.kpis.avgCostUsd, 4)} per convo`} />
        <KpiTile label="Positive ratings" value={data.kpis.ratingsCount === 0 ? "—" : `${data.kpis.positivePct}%`} hint={`${data.kpis.ratingsCount} rated`} />
      </section>

      {/* Two-up: top topics + status mix */}
      <section className="grid gap-5 lg:grid-cols-2">
        <Card className="p-5">
          <h2 className="mb-4 text-lg font-semibold">Top topics</h2>
          {data.topTopics.length === 0 ? (
            <p className="text-sm text-app-muted">No classified topics yet.</p>
          ) : (
            <div className="space-y-3">
              {data.topTopics.map((t) => {
                const max = data.topTopics[0].count || 1
                const pct = (t.count / max) * 100
                return (
                  <div key={t.key}>
                    <div className="mb-1 flex justify-between text-sm">
                      <span className="text-app-text">{t.name}</span>
                      <span className="text-app-muted">{t.count.toLocaleString()}</span>
                    </div>
                    <div className="h-2 overflow-hidden rounded-full bg-white/5">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-brand-red to-vip-gold"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </Card>

        <Card className="p-5">
          <h2 className="mb-4 text-lg font-semibold">Status mix</h2>
          {data.statusMix.length === 0 ? (
            <p className="text-sm text-app-muted">No conversations in window.</p>
          ) : (
            <div className="space-y-3">
              {data.statusMix.map((row) => (
                <div key={row.state} className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2">
                    <Badge tone={toneForState(row.state)}>{row.state}</Badge>
                    <span className="text-app-muted">{row.count.toLocaleString()}</span>
                  </span>
                  <span className="text-app-text">{row.pct}%</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </section>

      {/* Agent leaderboard */}
      <section>
        <Card className="overflow-hidden p-0">
          <header className="border-b border-border p-4">
            <h2 className="text-lg font-semibold">Agent leaderboard</h2>
            <p className="text-sm text-app-muted">Sorted by conversations handled.</p>
          </header>
          {data.agentLeaderboard.length === 0 ? (
            <div className="p-6 text-sm text-app-muted">No agents yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-xs uppercase tracking-wide text-app-subtle">
                  <th className="px-4 py-2 text-left">Agent</th>
                  <th className="px-4 py-2 text-right">Convos</th>
                  <th className="px-4 py-2 text-right">Resolved</th>
                  <th className="px-4 py-2 text-right">Positive</th>
                  <th className="px-4 py-2 text-right">Cost</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.agentLeaderboard.map((row) => (
                  <tr key={row.agentId} className="text-app-text">
                    <td className="px-4 py-3">
                      <div className="font-medium">{row.name}</div>
                      <div className="text-xs text-app-muted">{row.key}</div>
                    </td>
                    <td className="px-4 py-3 text-right text-app-muted">{row.convos.toLocaleString()}</td>
                    <td className="px-4 py-3 text-right text-vip-goldSoft">{row.resolvedPct}%</td>
                    <td className="px-4 py-3 text-right">
                      {row.ratingsCount === 0 ? (
                        <span className="text-app-subtle">—</span>
                      ) : (
                        <span className="text-vip-goldSoft">{row.positivePct}%</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right text-app-muted">{fmtMoney(row.costUsd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </section>

      {/* Recent activity */}
      <section>
        <Card className="p-5">
          <h2 className="mb-4 text-lg font-semibold">Recent activity</h2>
          {data.recentActivity.length === 0 ? (
            <p className="text-sm text-app-muted">No recent terminal conversations.</p>
          ) : (
            <ul className="space-y-2 text-sm">
              {data.recentActivity.map((row) => (
                <li key={row.id} className="flex items-center justify-between gap-3 rounded-control bg-white/5 px-3 py-2">
                  <span className="truncate">
                    <span className="text-app-text">{row.title}</span>
                    <span className="ml-2 text-app-muted">· {row.agent}</span>
                  </span>
                  <span className="flex items-center gap-2 text-xs text-app-subtle">
                    <Badge tone={toneForState(row.state)}>{row.state}</Badge>
                    {fmtTs(row.updatedAt)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </section>
    </AppShell>
  )
}

function KpiTile({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <Card className="p-5">
      <div className="text-sm text-app-muted">{label}</div>
      <div className="mt-2 text-3xl font-semibold tracking-tight">{value}</div>
      {hint && <div className="mt-1 text-xs text-app-subtle">{hint}</div>}
    </Card>
  )
}

function fmtMoney(n: number, decimals = 2): string {
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`
}

function fmtTs(iso: string): string {
  return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
}

// Reusing the badge tone vocabulary so a color means the same thing
// across the app.
function toneForState(state: string): "red" | "warning" | "neutral" | "success" | "gold" {
  if (state === "escalated") return "red"
  if (state === "open" || state === "rage_locked") return "warning"
  if (state === "resolved" || state === "auto_resolved") return "success"
  return "neutral"
}
